import { alpha, Box, Divider, Menu, MenuItem, MenuProps, styled, Switch, Tooltip, Typography } from '@mui/material';
import Button from 'components/Common/Button';
import { IoMenuSharp } from 'react-icons/io5';
import { MdDone, MdKeyboardArrowDown, MdMoreVert, MdOutlineEdit } from 'react-icons/md';
import React, { useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { GoPlus } from 'react-icons/go';
import ExportIcon2 from 'assets/icons/ExportIcon2';
import DataTable from 'assets/icons/DataTable';
import SystemUpdate from 'assets/icons/SystemUpdate';
import Compas from 'assets/icons/Compas';
import CsvData from 'papaparse';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useAddColumn,
  useCreateWorksheet,
  useDeleteAgentWorksheet,
  useGetColumns,
  useGetRows,
  useGetWorksheet,
  useGetWorksheets,
  useUpdateColumn,
  useUpdateWorksheet
} from 'apis/business/agent/worksheet/hook';
import * as urls from 'routes/config';
import WorksheetTable from './WorksheetTable';
import WorkflowIcon from 'assets/icons/WorkflowIcon';
import TextIcon from 'assets/icons/TextIcon';
import VisibilityIcon from 'assets/icons/VisibilityIcon';
import Grid from '@mui/material/Grid2';
import Select from 'components/Common/Select';
import Input from 'components/Common/Input';
import SmallRow from 'assets/icons/SmallRowIcon';
import MediumRowIcon from 'assets/icons/MediumRowIcon';
import ExtraTallRowIcon from 'assets/icons/ExtraTallRowIcon';

export default function Worksheets() {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, agentId, worksheetId } = useParams();
  const [activeTab, setActiveTab] = useState<number | string | undefined>(worksheetId ?? undefined);
  const [filters, setFilters] = useState<any>({});
  const [sorting, setSorting] = useState<any>({});
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElDropdown, setAnchorElDropdown] = React.useState<null | HTMLElement>(null);
  const [anchorElColumn, setAnchorElColumn] = React.useState<null | HTMLElement>(null);
  const [anchorElFreeze, setAnchorElFreeze] = React.useState<null | HTMLElement>(null);
  const [anchorElAddSheet, setAnchorElAddSheet] = React.useState<null | HTMLElement>(null);
  const [anchorElFilter, setAnchorElFilter] = React.useState<null | HTMLElement>(null);
  const [anchorElSort, setAnchorElSort] = React.useState<null | HTMLElement>(null);
  const [anchorElRowHeight, setAnchorElRowHeight] = React.useState<null | HTMLElement>(null);
  const [freezeColumnCount, setFreezeColumnCount] = useState<number>(1);
  const [selectedHeight, setSelectedHeight] = useState(40);

  const open = Boolean(anchorEl);
  const openDropdown = Boolean(anchorElDropdown);
  const openColumn = Boolean(anchorElColumn);
  const openFreeze = Boolean(anchorElFreeze);
  const openRowHeight = Boolean(anchorElRowHeight);
  const openAddSheet = Boolean(anchorElAddSheet);
  const openFilter = Boolean(anchorElFilter);
  const openSort = Boolean(anchorElSort);

  const { data: worksheets } = useGetWorksheets();
  const { data: currentWorksheet } = useGetWorksheet();
  const { data: columnsData } = useGetColumns();
  const { data: rowsData } = useGetRows();

  const { mutateAsync: handleWorksheet } = useDeleteAgentWorksheet();
  const { mutateAsync: addColumn } = useAddColumn();
  const { mutateAsync: handleUpdateColumn } = useUpdateColumn();
  const { mutateAsync: handleWorksheetCreate } = useCreateWorksheet();
  const { mutateAsync: handleUpdateWorksheet } = useUpdateWorksheet();

  const handleDownloadCSV = () => {
    if (!currentWorksheet?.columns || !rowsData?.data) {
      console.error('No worksheet data available');
      return;
    }

    const headers = currentWorksheet.columns.map((col: any) => col.name);

    const rows = rowsData.data.map((row: any) => {
      return currentWorksheet.columns.map((col: any) => {
        const cell = row.cells.find((data: any) => data.column_id === col.id);
        return cell?.value_for_display || cell?.value || '';
      });
    });

    const csvData = CsvData.unparse([headers, ...rows]);

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentWorksheet.name || 'worksheet'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      filters: [{ column_id: '', operation: '', value: '' as string | boolean }],
      sorting: [{ column_id: '', order: '' }]
    }
  });
  const getFilters = useWatch({ control, name: 'filters' });

  useEffect(() => {
    getFilters.forEach((filter: any, index: number) => {
      if (filter.operation === 'present' || filter.operation === 'blank') {
        setValue(`filters.${index}.value`, true);
      }
    });
  }, [getFilters, setValue]);

  const { append } = useFieldArray({
    control,
    name: 'filters'
  });

  const { fields: sortingFields, append: appendSorting } = useFieldArray({
    control,
    name: 'sorting'
  });

  const onSubmit = (data: any) => {
    const formattedFilters = data.filters
      .filter((filter: any) => filter.column_id && filter.operation)
      .map((filter: any, index: number) => ({
        [`filter[${index}][column_id]`]: filter.column_id,
        [`filter[${index}][operation]`]: filter.operation,
        [`filter[${index}][value]`]: filter.value
      }))
      .reduce((acc: any, curr: any) => ({ ...acc, ...curr }), {});

    const formattedSorting: Record<string, any> = {};

    const validSorts = data.sorting.filter((sort: any) => sort.column_id);

    validSorts.forEach((sort: any, index: number) => {
      formattedSorting['sort[][column_id]'] = sort.column_id;
      formattedSorting['sort[][direction]'] = sort.order;
    });

    const finalParams = { ...formattedFilters, ...formattedSorting };

    if (Object.keys(finalParams).length > 0) {
      setFilters(finalParams);
      setSorting(finalParams);
    }

    handleFilterClose();
    handleSortClose();
  };

  useEffect(() => {
    if (currentWorksheet?.configuration?.freezeColumnCount !== undefined) {
      setFreezeColumnCount(currentWorksheet.configuration.freezeColumnCount);
    }
  }, [currentWorksheet]);

  const handleFreezeColumnChange = async (count: number) => {
    setFreezeColumnCount(count);

    if (currentWorksheet) {
      await handleUpdateWorksheet({
        worksheet: {
          name: currentWorksheet.name,
          configuration: {
            ...currentWorksheet.configuration,
            freezeColumnCount: count
          }
        }
      });
    }
  };

  useEffect(() => {
    if (currentWorksheet?.configuration?.rowHeight !== undefined) {
      setSelectedHeight(currentWorksheet.configuration.rowHeight);
    }
  }, [currentWorksheet]);

  const handleSelect = async (height: number) => {
    setSelectedHeight(height);

    if (currentWorksheet) {
      await handleUpdateWorksheet({
        worksheet: {
          name: currentWorksheet.name,
          configuration: {
            ...currentWorksheet.configuration,
            rowHeight: height
          }
        }
      });
    }
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElDropdown(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorElDropdown(null);
  };

  const handleColumnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElColumn(event.currentTarget);
  };

  const handleColumnClose = () => {
    setAnchorElColumn(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFreezeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElFreeze(event.currentTarget);
  };
  const handleFreezeClose = () => {
    setAnchorElFreeze(null);
  };

  const handleRowHeightClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElRowHeight(event.currentTarget);
  };
  const handleRowHeightClose = () => {
    setAnchorElRowHeight(null);
  };

  const handleAddSheetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElAddSheet(event.currentTarget);
  };
  const handleAddSheetClose = () => {
    setAnchorElAddSheet(null);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElFilter(event.currentTarget);
  };
  const handleFilterClose = () => {
    setAnchorElFilter(null);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElSort(event.currentTarget);
  };
  const handleSortClose = () => {
    setAnchorElSort(null);
  };

  const isAddRoute = location.pathname.includes('add');
  const isEditRoute = location.pathname.includes('edit');
  const isImportRoute = location.pathname.includes('import');

  const hiddenColumnsCount = currentWorksheet?.columns.filter((column) => column.hidden).length;

  useEffect(() => {
    if (worksheets?.data?.length && !isAddRoute && !isEditRoute && !isImportRoute) {
      setActiveTab(worksheetId ?? worksheets.data[0].id);
      navigate(`${urls.worksheetBase(workspaceId, agentId)}/${worksheetId ?? worksheets.data[0].id}`);
    }
  }, [
    workspaceId,
    agentId,
    worksheets?.data,
    setActiveTab,
    navigate,
    isAddRoute,
    isEditRoute,
    isImportRoute,
    worksheetId
  ]);

  const handleHideColumn = (id: number, data: boolean) => {
    handleUpdateColumn({
      columnId: String(id),
      payload: {
        column: {
          hidden: data
        }
      }
    });
  };

  return (
    <>
      <Typography variant='h6' fontWeight='500' marginBottom={2}>
        Worksheet
      </Typography>
      <Box display='flex' gap={2} alignItems='center'>
        <Button
          color='secondary'
          variant='outlined'
          sx={{
            borderRadius: 1,
            minWidth: 'unset',
            padding: '6px 10px',
            borderColor: '#ECECEC',
            bgcolor: 'white'
          }}
        >
          <IoMenuSharp size={21} color='black' />
        </Button>
        <Box border='1px solid #ECECEC' overflow='auto' borderRadius={0.5} display='flex' alignItems='center'>
          {worksheets?.data?.map((worksheet, index) => (
            <Link to={`${urls.worksheetBase(workspaceId, agentId)}/${worksheet.id}`}>
              <Box
                key={worksheet.id}
                padding='8px 12px'
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                borderRight='1px solid #ECECEC'
                sx={{
                  bgcolor: Number(activeTab) === worksheet.id ? 'white' : 'inherit',
                  cursor: 'pointer',
                  width: 'auto',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => {
                  setActiveTab(worksheet.id);
                }}
              >
                <Typography fontSize={14} sx={{ marginRight: '24px' }}>
                  {worksheet.name}
                </Typography>
                {index !== 0 && (
                  <Typography
                    display='flex'
                    id='worksheet-more'
                    aria-controls={open ? 'worksheet-menu' : undefined}
                    aria-haspopup='true'
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                  >
                    <MdMoreVert size={24} color='#777777' />
                  </Typography>
                )}
              </Box>
            </Link>
          ))}
        </Box>
        <Button
          id='add-sheet-button'
          aria-controls={open ? 'add-sheet' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleAddSheetClick}
          color='secondary'
          variant='outlined'
          sx={{
            borderRadius: 1,
            minWidth: 'unset',
            padding: '6px 10px',
            borderColor: '#ECECEC',
            bgcolor: 'white'
          }}
        >
          <Typography display='flex'>
            <FaPlus size={16} color='#777777' />
          </Typography>
        </Button>
      </Box>

      <Box display='flex' alignItems='center' my='20px' justifyContent='space-between'>
        <Box display='flex' alignItems='center' gap={1}>
          <Button
            id='open-filter-button'
            aria-controls={open ? 'open-filter' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            onClick={handleFilterClick}
            color='secondary'
            variant='outlined'
            sx={{
              borderRadius: 1,
              minWidth: 'unset',
              padding: '6px 10px',
              borderColor: '#ECECEC',
              bgcolor: 'white'
            }}
            endIcon={<MdKeyboardArrowDown />}
          >
            {' '}
            Filters
          </Button>
          <Button
            id='open-sort-button'
            aria-controls={open ? 'open-sort' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            onClick={handleSortClick}
            color='secondary'
            variant='outlined'
            sx={{
              borderRadius: 1,
              minWidth: 'unset',
              padding: '6px 10px',
              borderColor: '#ECECEC',
              bgcolor: 'white'
            }}
            endIcon={<MdKeyboardArrowDown />}
          >
            {' '}
            Sort
          </Button>
          <Button
            sx={{
              whiteSpace: 'nowrap',
              bgcolor: 'white',
              borderRadius: 1,
              borderColor: '#ECECEC',
              width: '170px',
              minWidth: '170px'
            }}
            color='secondary'
            variant='outlined'
            id='hide-column-button'
            aria-controls={open ? 'hide-column-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            startIcon={<VisibilityIcon />}
            onClick={handleColumnClick}
            fullWidth
          >
            {hiddenColumnsCount ? `${hiddenColumnsCount} Hidden Columns` : 'Hide Columns'}
          </Button>
          <Tooltip title='Freeze columns' arrow placement='top'>
            <Button
              color='secondary'
              variant='outlined'
              sx={{
                borderRadius: 1,
                minWidth: 'unset',
                padding: '6px 10px',
                borderColor: '#ECECEC',
                bgcolor: 'white'
              }}
              id='freeze-column'
              aria-controls={open ? 'open-freeze-column' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
              onClick={handleFreezeClick}
            >
              <DataTable />
            </Button>
          </Tooltip>
          <Tooltip title='Download CSV' arrow placement='top'>
            <Button
              color='secondary'
              variant='outlined'
              sx={{
                borderRadius: 1,
                minWidth: 'unset',
                padding: '6px 10px',
                borderColor: '#ECECEC',
                bgcolor: 'white'
              }}
              onClick={handleDownloadCSV}
            >
              <SystemUpdate />
            </Button>
          </Tooltip>
          <Tooltip title='Row height' arrow placement='top'>
            <Button
              color='secondary'
              variant='outlined'
              sx={{
                borderRadius: 1,
                minWidth: 'unset',
                padding: '6px 10px',
                borderColor: '#ECECEC',
                bgcolor: 'white'
              }}
              id='row-height'
              aria-controls={open ? 'open-row-height' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
              onClick={handleRowHeightClick}
            >
              <Compas />
            </Button>
          </Tooltip>
        </Box>
        <Box display='flex' alignItems='center' gap={2}>
          <Button
            sx={{ textWrap: 'nowrap', minWidth: 'unset', background: 'white' }}
            color='secondary'
            variant='outlined'
            fullWidth
            startIcon={<ExportIcon2 />}
            onClick={() => {
              navigate(`${urls.worksheetBase(workspaceId, agentId)}/${worksheetId}/import-csv`);
            }}
          >
            Import CSV
          </Button>
          <Button
            sx={{ textWrap: 'nowrap', minWidth: 'unset' }}
            color='primary'
            startIcon={<GoPlus />}
            onClick={handleDropdownClick}
          >
            Add Column
          </Button>
        </Box>
      </Box>
      {activeTab && (
        <WorksheetTable
          worksheetData={worksheets?.data.find((item: any) => item.id === Number(activeTab))}
          filters={filters}
          handleFreezeColumnChange={freezeColumnCount}
          sorting={sorting}
          selectedHeight={selectedHeight}
        />
      )}
      <Menu
        id='worksheet-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'worksheet-more'
        }}
      >
        <MenuItem onClick={handleClose}>
          <Box
            display='flex'
            alignItems='center'
            gap={1}
            onClick={() => {
              navigate(`${urls.worksheetBase(workspaceId, agentId)}/${worksheetId}/edit`);
            }}
          >
            <MdOutlineEdit color='grey' />
            <Typography fontSize={14}>Rename Sheet</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleClose}>
          <Box display='flex' alignItems='center' gap={1} onClick={() => handleWorksheet(String(worksheetId))}>
            <RiDeleteBin6Line color='red' />
            <Typography color='red' fontSize={14}>
              Delete Sheet
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
      <Menu
        id='add-column-dropdown'
        anchorEl={anchorElDropdown}
        open={openDropdown}
        onClose={handleDropdownClose}
        MenuListProps={{
          'aria-labelledby': 'add-column-button'
        }}
        sx={{ marginTop: '2px' }}
      >
        <MenuItem
          onClick={() => {
            handleDropdownClose();
            navigate('add-workflow');
          }}
        >
          <WorkflowIcon />
          <Box>
            <Typography fontSize={14}>WorkFlow</Typography>
          </Box>
        </MenuItem>
        <Divider />
        <Typography fontSize={10} sx={{ marginLeft: 2, color: 'grey' }}>
          DATA
        </Typography>
        <MenuItem
          onClick={() => {
            handleDropdownClose();
            addColumn({
              column: {
                data_type: 'text'
              }
            });
          }}
        >
          <TextIcon />
          <Box>
            <Typography fontSize={14}>Text</Typography>
          </Box>
        </MenuItem>
      </Menu>
      <Menu
        id='hide-column-menu'
        anchorEl={anchorElColumn}
        open={openColumn}
        onClose={handleColumnClose}
        MenuListProps={{
          'aria-labelledby': 'hide-column-button'
        }}
        slotProps={{ paper: { sx: { width: 380 } } }}
      >
        <Box p={1}>
          {currentWorksheet?.columns?.map?.((item, index) => (
            <Box component='label' key={index}>
              <MenuItem sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>{item.name}</Typography>
                <Switch
                  size='small'
                  defaultChecked={item?.hidden}
                  onChange={(e) => handleHideColumn(item.id, e.target.checked)}
                />
              </MenuItem>
            </Box>
          ))}
        </Box>
      </Menu>
      <Menu
        id='open-freeze-column'
        anchorEl={anchorElFreeze}
        open={openFreeze}
        onClose={handleFreezeClose}
        MenuListProps={{
          'aria-labelledby': 'freeze-column'
        }}
      >
        <Box p={1} width={200}>
          <Typography sx={{ fontSize: '12px', color: 'grey' }}> FREEZE COLUMNS </Typography>
          <MenuItem
            sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '0' }}
            onClick={() => handleFreezeColumnChange(1)}
          >
            First Column {freezeColumnCount === 1 && <MdDone />}
          </MenuItem>
          <MenuItem
            sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '0' }}
            onClick={() => handleFreezeColumnChange(2)}
          >
            First 2 Column {freezeColumnCount === 2 && <MdDone />}
          </MenuItem>
          <MenuItem
            sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '0' }}
            onClick={() => handleFreezeColumnChange(3)}
          >
            First 3 Column {freezeColumnCount === 3 && <MdDone />}
          </MenuItem>
        </Box>
      </Menu>
      <Menu
        id='open-row-height'
        anchorEl={anchorElRowHeight}
        open={openRowHeight}
        onClose={handleRowHeightClose}
        MenuListProps={{
          'aria-labelledby': 'row-height'
        }}
      >
        <Box p={1} width={200}>
          <Typography sx={{ fontSize: '12px', color: 'grey' }}>ROW HEIGHT</Typography>
          {[
            { label: 'Small', value: 40, icon: <SmallRow /> },
            { label: 'Medium', value: 80, icon: <MediumRowIcon /> },
            { label: 'Tall', value: 150, icon: <MediumRowIcon /> },
            { label: 'Extra Tall', value: 200, icon: <ExtraTallRowIcon /> }
          ].map((item) => (
            <MenuItem
              key={item.value}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                padding: '0'
              }}
              onClick={() => handleSelect(item.value)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {item.icon}
                {item.label}
              </Box>{' '}
              {selectedHeight === item.value && <MdDone />}
            </MenuItem>
          ))}
        </Box>
      </Menu>
      <Menu
        id='add-sheet'
        anchorEl={anchorElAddSheet}
        open={openAddSheet}
        onClose={handleAddSheetClose}
        MenuListProps={{
          'aria-labelledby': 'add-sheet-button'
        }}
      >
        <Box p={1} width={200}>
          <Typography sx={{ fontSize: '12px', color: 'grey' }}> CREATE SHEET </Typography>
          <MenuItem
            sx={{ padding: 0, marginTop: 1 }}
            onClick={() => {
              handleWorksheetCreate();
              handleAddSheetClose();
            }}
          >
            Blank
          </MenuItem>
        </Box>
      </Menu>
      <StyledMenu
        id='open-filter'
        anchorEl={anchorElFilter}
        open={openFilter}
        onClose={handleFilterClose}
        MenuListProps={{
          'aria-labelledby': 'open-filter-button'
        }}
      >
        <Box sx={{ padding: 2 }}>
          <Typography> Filters</Typography>
          <Divider />
          <Box component='form' onSubmit={handleSubmit(onSubmit)}>
            {getFilters.map((field, index) => {
              const operation = watch(`filters.${index}.operation`);
              const columnId = watch(`filters.${index}.column_id`);
              return (
                <Grid container spacing={2} marginTop={2} alignItems='center'>
                  <Grid size={4} height={40}>
                    <Select
                      name={`filters.${index}.column_id`}
                      label='column'
                      size='small'
                      control={control}
                      defaultValue={filters.column_id}
                      options={
                        columnsData?.data.map((col: any) => ({
                          label: col.name,
                          value: col.id
                        })) ?? []
                      }
                    />
                  </Grid>
                  <Grid size={4} height={40}>
                    <Select
                      name={`filters.${index}.operation`}
                      label='operation'
                      size='small'
                      control={control}
                      options={[
                        { label: 'Is Not Empty', value: 'present' },
                        { label: 'Is Empty', value: 'blank' },
                        { label: 'Contains', value: 'cont' },
                        { label: 'Does Not Contain', value: 'not_cont' },
                        { label: 'Is Equal To', value: 'eq' },
                        { label: 'Is Not Equal To', value: 'not_eq' }
                      ]}
                    />
                  </Grid>
                  <Grid size={4} height={40}>
                    <Input
                      {...field}
                      name={`filters.${index}.value`}
                      label='Value'
                      control={control}
                      size='small'
                      disabled={operation === 'present' || operation === 'blank' || !columnId}
                      value={field.operation === 'present' || field.operation === 'blank' ? '' : field.value || ''}
                      onChange={(e) => setValue(`filters.${index}.value`, e.target.value)}
                    />
                  </Grid>
                </Grid>
              );
            })}
            <Button
              sx={{
                borderRadius: 1,
                minWidth: 'unset',
                borderColor: '#ECECEC',
                width: '100%',
                marginY: '8px',
                bgcolor: '#09090b0a',
                color: 'black',
                height: 'auto'
              }}
              onClick={() => append({ column_id: '', operation: '', value: '' })}
            >
              + Add Filter
            </Button>
            <Divider />
            <Box display='flex' gap='8px'>
              <Button
                sx={{
                  borderRadius: 1,
                  minWidth: 'unset',
                  padding: '6px 20px',
                  borderColor: '#ECECEC',
                  marginY: '8px'
                }}
                type='submit'
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </StyledMenu>
      <StyledMenu
        id='open-sort'
        anchorEl={anchorElSort}
        open={openSort}
        onClose={handleSortClose}
        MenuListProps={{
          'aria-labelledby': 'open-sort-button'
        }}
      >
        <Box sx={{ padding: 2 }}>
          <Typography> Sort </Typography>
          <Divider />
          <Box component='form' onSubmit={handleSubmit(onSubmit)}>
            {sortingFields.map((field, index) => (
              <Grid container spacing={2} marginTop={2} alignItems='center' key={field.id}>
                <Grid size={6}>
                  <Select
                    name={`sorting.${index}.column_id`}
                    label='Select Column'
                    size='small'
                    control={control}
                    options={
                      columnsData?.data.map((col: any) => ({
                        label: col.name,
                        value: col.id
                      })) ?? []
                    }
                  />
                </Grid>
                <Grid size={6}>
                  <Select
                    name={`sorting.${index}.order`}
                    label='Direction'
                    size='small'
                    control={control}
                    options={[
                      { label: 'Ascending', value: 'asc' },
                      { label: 'Descending', value: 'desc' }
                    ]}
                  />
                </Grid>
              </Grid>
            ))}
            <Button
              sx={{
                borderRadius: 1,
                minWidth: 'unset',
                borderColor: '#ECECEC',
                width: '100%',
                marginY: '8px',
                bgcolor: '#09090b0a',
                color: 'black',
                height: 'auto'
              }}
              onClick={() => appendSorting({ column_id: '', order: '' })}
            >
              + Add Sort
            </Button>
            <Divider />
            <Box display='flex' gap='8px'>
              <Button
                sx={{
                  borderRadius: 1,
                  minWidth: 'unset',
                  padding: '6px 20px',
                  borderColor: '#ECECEC',
                  marginY: '8px'
                }}
                type='submit'
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </StyledMenu>
      <Outlet />
    </>
  );
}

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(10),
    minWidth: 500,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0'
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(8)
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
      }
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300]
    })
  }
}));
